import React, { useEffect, useState, createElement } from 'react';
import { MailIcon, ArrowLeftIcon, CalendarIcon, PresentationIcon, ArrowRightIcon, PlayCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
declare global {
  interface Window {
    Tally?: {
      openPopup: (formId: string, options?: Record<string, any>) => void;
      closePopup: (formId: string) => void;
    };
  }
}
export function InvestorDeckPage() {
  const [tallyReady, setTallyReady] = useState(false);
  useEffect(() => {
    if (window.Tally) {
      setTallyReady(true);
      return;
    }
    const loadTally = () => {
      const existing = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
      if (existing) {
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 200);
        setTimeout(() => clearInterval(interval), 10000);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = () => {
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 100);
        setTimeout(() => clearInterval(interval), 5000);
      };
      document.head.appendChild(script);
    };
    loadTally();
  }, []);
  const openInvestorForm = () => {
    if (window.Tally) {
      window.Tally.openPopup('44258r', {
        layout: 'modal',
        width: 700,
        autoClose: 5000
      });
    } else {
      window.open('https://tally.so/r/44258r', '_blank');
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-2707">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-2708">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between" data-id="element-2709">
          <div className="flex items-center space-x-2 md:space-x-4 min-w-0" data-id="element-2710">
            <Link to="/" className="text-gray-500 hover:text-[#631bb7] transition-colors shrink-0" data-id="element-2711">
              <ArrowLeftIcon size={18} data-id="element-2712" />
            </Link>
            <div className="h-5 w-px bg-gray-200 shrink-0" data-id="element-2713"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-5 md:h-6 shrink-0" data-id="element-2714" />
            <span className="bg-gray-100 text-gray-600 text-[10px] md:text-xs font-semibold px-1.5 md:px-2 py-0.5 md:py-1 rounded-full uppercase tracking-wide shrink-0 hidden sm:inline-block" data-id="element-2715">
              Investor Portal
            </span>
          </div>
          <Link to="/invest" className="bg-[#631bb7] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#521099] transition-colors shrink-0 no-underline" data-id="element-2716">
            Invest
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6 md:space-y-8" data-id="element-2717">
        {/* Welcome / Hero */}
        <section className="text-center max-w-3xl mx-auto" data-id="element-2718">
          <h1 className="text-[2rem] md:text-[3.25rem] font-semibold text-[#111111] leading-[1.15] tracking-tight mb-2 md:mb-3" data-id="element-2719">
            Welcome to the Caveo Investor Portal
          </h1>
          <p className="text-base md:text-lg text-gray-500 leading-relaxed" data-id="element-2720">
            Access our strategic overview and product roadmap as we redefine
            family digital safety.
          </p>
        </section>

        {/* Video Overview */}
        <section className="mx-auto" data-id="element-2721">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2722">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50" data-id="element-2723">
              <PlayCircleIcon size={18} className="text-[#631bb7]" data-id="element-2724" />
              <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-2725">
                Video Overview
              </h2>
            </div>
            <div className="w-full relative" style={{
            paddingBottom: '56.25%',
            height: 0
          }} data-id="element-2726">
              <iframe src="https://drive.google.com/file/d/16XEJ5YgzcR2ffQXuc6t07ucUXlRq1lVf/preview" allow="autoplay" allowFullScreen className="absolute top-0 left-0 w-full h-full border-0" title="Caveo Overview Video" data-id="element-2727" />
            </div>
          </div>
        </section>

        {/* Review — 4 visual cards */}
        <section className="mx-auto" data-id="element-2728">
          <p className="text-[10px] md:text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 md:mb-3" data-id="element-2729">
            Review
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-2730">
            {/* Row 1: Business Plan + Market Research */}
            {/* Business Plan Card */}
            <Link to="/investor-plan" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all no-underline overflow-hidden flex flex-col" data-id="element-2731">
              <div className="p-4 pb-3" data-id="element-2732">
                <p className="font-bold text-[#631bb7] uppercase tracking-wider mb-1.5 text-[14px]" data-id="element-2733">
                  Business Plan
                </p>
                <h2 className="text-sm md:text-base font-normal text-[#111111] leading-snug mb-3" data-id="element-2734">
                  Read an abridged version of our business plan
                </h2>
                <span className="inline-block bg-[#631bb7] text-white px-4 py-2 rounded-lg font-medium text-sm group-hover:bg-[#521099] transition-colors text-center" data-id="element-2735">
                  View Plan
                </span>
              </div>
              <div className="flex-1 px-3 pb-3 flex flex-col justify-end" data-id="element-2736">
                <div className="rounded-lg overflow-hidden border border-gray-100 bg-gray-50 h-[140px]" data-id="element-2737">
                  <img src="https://cdn.magicpatterns.com/uploads/1zrCQ9Gr3ZYbFmE2483dQT/Screenshot_2026-03-09_at_1.30.11_AM.png" alt="Business Plan preview" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300 brightness-[0.92] saturate-[0.85]" data-id="element-2738" />
                </div>
              </div>
            </Link>

            {/* Market Research Card */}
            <Link to="/investor-research" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all no-underline overflow-hidden flex flex-col" data-id="element-2739">
              <div className="p-4 pb-3" data-id="element-2740">
                <p className="font-bold text-[#631bb7] uppercase tracking-wider mb-1.5 text-[14px]" data-id="element-2741">
                  Market Research
                </p>
                <h2 className="text-sm md:text-base font-normal text-[#111111] leading-snug mb-3" data-id="element-2742">
                  Review our market validation report
                </h2>
                <span className="inline-block bg-[#631bb7] text-white px-4 py-2 rounded-lg font-medium text-sm group-hover:bg-[#521099] transition-colors text-center" data-id="element-2743">
                  View Report
                </span>
              </div>
              <div className="flex-1 px-3 pb-3 flex flex-col justify-end" data-id="element-2744">
                <div className="rounded-lg overflow-hidden border border-gray-100 bg-gray-50 h-[140px]" data-id="element-2745">
                  <img src="https://cdn.magicpatterns.com/uploads/oqzJtp6qNhMqD6TrzSPWrV/Screenshot_2026-03-09_at_8.32.13_AM.png" alt="Market Research preview" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300 brightness-[0.92] saturate-[0.85]" data-id="element-2746" />
                </div>
              </div>
            </Link>

            {/* Row 2: Product Overview + Current Roadmap */}
            {/* Product Overview Card */}
            <Link to="/investor-overview" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all no-underline overflow-hidden flex flex-col" data-id="element-2747">
              <div className="p-4 pb-3" data-id="element-2748">
                <p className="font-bold text-[#631bb7] uppercase tracking-wider mb-1.5 text-[14px]" data-id="element-2749">
                  Product Overview
                </p>
                <h2 className="text-sm md:text-base font-normal text-[#111111] leading-snug mb-3" data-id="element-2750">
                  Review our approach & offering
                </h2>
                <span className="inline-block bg-[#631bb7] text-white px-4 py-2 rounded-lg font-medium text-sm group-hover:bg-[#521099] transition-colors text-center" data-id="element-2751">
                  View Overview
                </span>
              </div>
              <div className="flex-1 px-3 pb-3 flex flex-col justify-end" data-id="element-2752">
                <div className="rounded-lg overflow-hidden border border-gray-100 bg-gray-50 h-[140px]" data-id="element-2753">
                  <img src="https://cdn.magicpatterns.com/uploads/93CQkpzDHgFWnEcf4Lj9VY/Solution_Tile.png" alt="Product Overview preview" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300 brightness-[0.92] saturate-[0.85]" data-id="element-2754" />
                </div>
              </div>
            </Link>

            {/* Current Roadmap Card */}
            <Link to="/investor-roadmap" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all no-underline overflow-hidden flex flex-col" data-id="element-2755">
              <div className="p-4 pb-3" data-id="element-2756">
                <p className="font-bold text-[#631bb7] uppercase tracking-wider mb-1.5 text-[14px]" data-id="element-2757">
                  Current Roadmap
                </p>
                <h2 className="text-sm md:text-base font-normal text-[#111111] leading-snug mb-3" data-id="element-2758">
                  Review our product roadmap
                </h2>
                <span className="inline-block bg-[#631bb7] text-white px-4 py-2 rounded-lg font-medium text-sm group-hover:bg-[#521099] transition-colors text-center" data-id="element-2759">
                  View Roadmap
                </span>
              </div>
              <div className="flex-1 px-3 pb-3 flex flex-col justify-end" data-id="element-2760">
                <div className="rounded-lg overflow-hidden border border-gray-100 bg-gray-50 h-[140px]" data-id="element-2761">
                  <img src="https://cdn.magicpatterns.com/uploads/h36X9PXxxPnRR1zyfhF4pm/Roadmap.png" alt="Current Roadmap preview" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300 brightness-[0.92] saturate-[0.85]" data-id="element-2762" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mx-auto" data-id="element-2763">
          <p className="text-[10px] md:text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 md:mb-3" data-id="element-2764">
            Next Steps
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-id="element-2765">
            {/* Email Us */}
            <a href="mailto:jbp@caveohq.com" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all p-5 md:p-6 flex flex-col items-center text-center no-underline" data-id="element-2766">
              <div className="w-10 h-10 bg-[#f0ebf8] rounded-full flex items-center justify-center mb-3 shrink-0" data-id="element-2767">
                <MailIcon size={20} className="text-[#631bb7]" data-id="element-2768" />
              </div>
              <h3 className="font-bold text-base text-[#111111] mb-2" data-id="element-2769">
                Email Us
              </h3>
              <p className="text-xs text-gray-500 mb-4 flex-1" data-id="element-2770">
                Send us a due diligence question or request additional
                materials.
              </p>
              <span className="bg-[#631bb7] text-white w-full text-center rounded-lg font-medium text-sm py-2.5 group-hover:bg-[#521099] transition-colors block" data-id="element-2771">
                Send Email
              </span>
            </a>

            {/* Book a Meeting */}
            <a href="https://calendar.app.google/EnoqEH7z4cY89jjN8" target="_blank" rel="noopener noreferrer" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all p-5 md:p-6 flex flex-col items-center text-center no-underline" data-id="element-2772">
              <div className="w-10 h-10 bg-[#f0ebf8] rounded-full flex items-center justify-center mb-3 shrink-0" data-id="element-2773">
                <CalendarIcon size={20} className="text-[#631bb7]" data-id="element-2774" />
              </div>
              <h3 className="font-bold text-base text-[#111111] mb-2" data-id="element-2775">
                Book a Meeting
              </h3>
              <p className="text-xs text-gray-500 mb-4 flex-1" data-id="element-2776">
                Schedule a deep dive into our product roadmap and strategy.
              </p>
              <span className="bg-[#631bb7] text-white w-full text-center rounded-lg font-medium text-sm py-2.5 group-hover:bg-[#521099] transition-colors block" data-id="element-2777">
                Schedule Now
              </span>
            </a>

            {/* Invest Now */}
            <Link to="/invest" className="group bg-[#fafafa] rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#8b5dc8] transition-all p-5 md:p-6 flex flex-col items-center text-center no-underline w-full" data-id="element-2778">
              <div className="w-10 h-10 bg-[#f0ebf8] rounded-full flex items-center justify-center mb-3 shrink-0" data-id="element-2779">
                <ArrowRightIcon size={20} className="text-[#631bb7]" data-id="element-2780" />
              </div>
              <h3 className="font-bold text-base text-[#111111] mb-2" data-id="element-2781">
                Invest Now
              </h3>
              <p className="text-xs text-gray-500 mb-4 flex-1" data-id="element-2782">
                Review disclosures, the SAFE agreement, and submit your
                interest.
              </p>
              <span className="bg-[#631bb7] text-white w-full text-center rounded-lg font-medium text-sm py-2.5 group-hover:bg-[#521099] transition-colors block" data-id="element-2783">
                Get Started
              </span>
            </Link>
          </div>
        </section>

        {/* Pitch Deck */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mx-auto" data-id="element-2784">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50" data-id="element-2785">
            <PresentationIcon size={18} className="text-[#631bb7]" data-id="element-2786" />
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-2787">
              Pitch Deck
            </h2>
          </div>
          <div className="w-full" style={{
          aspectRatio: '16/9'
        }} data-id="element-2788">
            <iframe src="https://docs.google.com/presentation/d/1DDAemu62thxS5XgH-nUiftFnvyhFoQviVl-XCZ8Ijuo/embed?start=false&loop=false&delayms=3000" className="w-full h-full border-0" allowFullScreen title="Caveo Pitch Deck" data-id="element-2789" />
          </div>
        </section>

        {/* Trust / Logo Strip */}
        <section className="mx-auto" data-id="element-2790">
          <p className="text-center text-xs md:text-sm font-medium text-gray-400 uppercase tracking-wider mb-4 md:mb-6" data-id="element-2791">
            Built by leaders from
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 md:gap-x-12 gap-y-4 grayscale opacity-50" data-id="element-2792">
            <img src="https://cdn.magicpatterns.com/uploads/nmqUJqLxx2VskE1njSdPet/Experian_logo.svg.png" alt="Experian" className="h-6 md:h-8 max-w-[100px] md:max-w-[120px] object-contain" data-id="element-2793" />
            <img src="https://cdn.magicpatterns.com/uploads/fnXGBwy5K7uyX2UvFt6ups/Asana_logo.svg.png" alt="Asana" className="h-5 md:h-7 max-w-[80px] md:max-w-[100px] object-contain" data-id="element-2794" />
            <img src="https://cdn.magicpatterns.com/uploads/jrBECYDpvuXUfYDgiVyTh2/drift_logo_fixed.png" alt="Drift" className="h-5 md:h-7 max-w-[70px] md:max-w-[90px] object-contain" data-id="element-2795" />
            <img src="https://cdn.magicpatterns.com/uploads/vjVVFtuWSA5UFtZmC7CMpG/Intel-Logo.png" alt="Intel" className="h-6 md:h-8 max-w-[60px] md:max-w-[70px] object-contain" data-id="element-2796" />
            <img src="https://cdn.magicpatterns.com/uploads/gifY524awm2QkgRHAkporM/NBCUniversal_2026_Logo.svg" alt="NBCUniversal" className="h-5 md:h-7 max-w-[110px] md:max-w-[140px] object-contain" data-id="element-2797" />
          </div>
        </section>
      </main>

      <footer className="bg-white border-t border-gray-200 py-6 md:py-8 mt-auto" data-id="element-2798">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-xs md:text-sm text-gray-500" data-id="element-2799">
          <p data-id="element-2800">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-1 md:mt-2 text-xs text-gray-400" data-id="element-2801">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}